import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams, Navigate } from "react-router-dom";
import { categoryPath } from "@/lib/paths";
import SubcategoryNav from "@/components/SubcategoryNav";
import { api } from "@/lib/api";
import SupplierFilterPanel, { RfqStrip } from "@/components/SupplierFilterPanel";
import ListingGrid from "@/components/ListingGrid";
import SEO from "@/components/SEO";
import ComingSoonCategory from "@/components/ComingSoonCategory";
import { StatusLine } from "@/components/CategoryGrid";
import { categorySeo } from "@/lib/seo";
import CategoryRFQModal from "@/components/CategoryRFQModal";
import { ChevronRight, ChevronDown, ChevronUp, LayoutGrid, Sparkles, MapPin, BadgeCheck, Globe } from "lucide-react";
import { toast } from "sonner";

export default function CategoryPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cat, setCat] = useState(null);
  const [listings, setListings] = useState({ items: [], total: 0 });
  const [rfqOpen, setRfqOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [subnavOpen, setSubnavOpen] = useState(false);
  const [geo, setGeo] = useState(null); // { country_code, region_code, city }

  // One-shot geo lookup, session-cached, so the smart location filter can highlight nearby regions
  useEffect(() => {
    try {
      const cached = sessionStorage.getItem("as_geoip");
      if (cached) { setGeo(JSON.parse(cached)); return; }
    } catch {}
    api.get("/geoip").then(({ data }) => {
      setGeo(data);
      try { sessionStorage.setItem("as_geoip", JSON.stringify(data)); } catch {}
    }).catch(() => {});
  }, []);

  // URL-driven filter state — every filter is a query param for shareability
  const activeTags = useMemo(() => {
    const raw = searchParams.get("tags") || "";
    return raw ? raw.split(",").filter(Boolean) : [];
  }, [searchParams]);
  const activeCountry      = searchParams.get("country_code") || "";
  const activeState        = searchParams.get("state_code") || "";
  const activeSupplierType = searchParams.get("supplier_type") || "";
  const activeSupplierTypes = useMemo(() => activeSupplierType.split(",").filter(Boolean), [activeSupplierType]);
  const verifiedOnly       = searchParams.get("verified") === "true";
  const tagQuery           = searchParams.get("tq") || "";
  const textQuery          = searchParams.get("q") || "";
  const customOem          = searchParams.get("oem") === "true";
  const [qInput, setQInput] = useState(textQuery);
  useEffect(() => { setQInput(textQuery); }, [textQuery]);
  // Search runs on the Search button / Enter key (not while typing).
  const submitSearch = (value) => updateParams({ q: (value ?? qInput).trim() });
  const [tagInput, setTagInput] = useState(tagQuery);
  useEffect(() => { setTagInput(tagQuery); }, [tagQuery]);
  useEffect(() => {
    if (tagInput.trim() === tagQuery.trim()) return;
    const t = setTimeout(() => updateParams({ tq: tagInput.trim() }), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagInput]);

  const updateParams = (next) => {
    const p = new URLSearchParams(window.location.search);
    Object.entries(next).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined || v === false) p.delete(k);
      else p.set(k, String(v));
    });
    setSearchParams(p, { replace: true });
  };

  const setActiveTags = (next) => {
    const clean = Array.from(new Set(next)).filter(Boolean);
    updateParams({ tags: clean.length === 0 ? "" : clean.join(",") });
  };
  const toggleTag = (tag) => {
    if (activeTags.includes(tag)) setActiveTags(activeTags.filter((t) => t !== tag));
    else setActiveTags([...activeTags, tag]);
  };

  useEffect(() => {
    setLoading(true);
    const qp = new URLSearchParams();
    if (activeCountry)      qp.set("country_code", activeCountry);
    if (activeState)        qp.set("state_code", activeState);
    if (activeSupplierType) qp.set("supplier_type", activeSupplierType);
    if (verifiedOnly)       qp.set("verified", "true");
    if (customOem)          qp.set("custom_oem", "true");
    const suffix = qp.toString() ? `?${qp.toString()}` : "";
    api.get(`/categories/${id}${suffix}`).then(({ data }) => {
      setCat(data);
    }).catch((err) => {
      if (err.response?.status !== 404) toast.error("Failed to load category");
    }).finally(() => setLoading(false));
  }, [id, activeCountry, activeState, activeSupplierType, verifiedOnly, customOem]);

  const [categoryTop, setCategoryTop] = useState([]);
  useEffect(() => {
    if (!cat) return;
    const param = cat.parent_id === null ? "parent_category_id" : "category_id";
    api.get(`/listings?${param}=${cat.id}&limit=4`).then(({ data }) => setCategoryTop(data.items || [])).catch(() => {});
  }, [cat]);

  const PAGE_SIZE = 48;
  const [loadingMore, setLoadingMore] = useState(false);
  const buildListingParams = (limit, skip) => {
    const param = cat.parent_id === null ? "parent_category_id" : "category_id";
    const qp = new URLSearchParams();
    qp.set(param, cat.id);
    qp.set("limit", String(limit));
    if (skip) qp.set("skip", String(skip));
    if (activeTags.length > 0) qp.set("tags", activeTags.join(","));
    if (tagQuery)           qp.set("tag_q", tagQuery);
    if (textQuery)          qp.set("q", textQuery);
    if (activeCountry)      qp.set("country_code", activeCountry);
    if (activeState)        qp.set("state_code", activeState);
    if (activeSupplierType) qp.set("supplier_type", activeSupplierType);
    if (verifiedOnly)       qp.set("verified", "true");
    if (customOem)          qp.set("custom_oem", "true");
    return qp;
  };
  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get(`/listings?${buildListingParams(PAGE_SIZE, listings.items.length).toString()}`);
      setListings((prev) => {
        const seen = new Set(prev.items.map((i) => i.id));
        return { ...data, items: [...prev.items, ...(data.items || []).filter((i) => !seen.has(i.id))] };
      });
    } catch { toast.error("Couldn't load more suppliers"); }
    finally { setLoadingMore(false); }
  };

  useEffect(() => {
    if (!cat) return;
    const qp = buildListingParams(PAGE_SIZE, 0);
    api.get(`/listings?${qp.toString()}`).then(({ data }) => setListings(data)).catch(() => toast.error("Failed to load suppliers"));
  }, [cat, activeTags, tagQuery, textQuery, activeCountry, activeState, activeSupplierType, verifiedOnly, customOem]);

  if (loading || !cat) return <div className="max-w-7xl mx-auto px-4 py-16 text-[#7D8C85]">Loading…</div>;
  if (cat.slug && id !== cat.slug) return <Navigate to={{ pathname: categoryPath(cat), search: window.location.search }} replace />;
  if (cat.parent_id === null && (cat.launch_status === "coming_soon" || (!cat.launch_status && cat.is_coming_soon))) return <ComingSoonCategory cat={cat} />;

  // Sparse-guard: hide tag chips that don't meet the supplier/product threshold from public view
  const allTagFilters = cat.tag_filters || [];
  const visibleTagFilters = allTagFilters.filter((t) => t.visible !== false);
  const grouped = visibleTagFilters.reduce((acc, t) => {
    const g = t.group || "Product types";
    (acc[g] = acc[g] || []).push(t);
    return acc;
  }, {});
  const groupOrder = Object.keys(grouped);

  // Quick-filter facets from the enriched category doc
  const qf = cat.quick_filters || { states: [], countries: [], supplier_types: [], verified_count: 0 };
  // Smart Location: bring the visitor's own region to the top of the state chips + tag it
  const nearCode = (geo && geo.region_code) ? geo.region_code.toUpperCase() : "";
  const nearCountry = (geo && geo.country_code) ? geo.country_code.toUpperCase() : "";
  const orderedStates = [...qf.states].sort((a, b) => {
    if (a.code === nearCode && b.code !== nearCode) return -1;
    if (b.code === nearCode && a.code !== nearCode) return 1;
    return b.count - a.count;
  });
  const orderedCountries = [...(qf.countries || [])].sort((a, b) => {
    if (a.code === nearCountry && b.code !== nearCountry) return -1;
    if (b.code === nearCountry && a.code !== nearCountry) return 1;
    return b.count - a.count;
  });
  const hasQuickFilters = orderedStates.length > 0 || orderedCountries.length > 0 || qf.supplier_types.length > 0 || qf.verified_count > 0;
  const anyFilterActive = activeTags.length > 0 || textQuery || activeCountry || activeState || activeSupplierType || verifiedOnly || customOem;
  const seo = categorySeo(cat, {
    stateLabel: activeState ? ((qf.states || []).find((s) => s.code === activeState)?.name || activeState) : "",
    countryLabel: activeCountry ? ((qf.countries || []).find((c) => c.code === activeCountry)?.name || activeCountry) : "",
    total: listings.total,
  });

  const advancedCount = (activeState ? 1 : 0) + (verifiedOnly ? 1 : 0) + activeTags.length;
  const chipCls = (on, near = false) => `text-[11px] px-2.5 py-1 border transition inline-flex items-center gap-1.5 ${on ? "bg-[#264839] text-white border-[#264839]" : near ? "bg-white text-[#264839] border-[#264839] font-semibold" : "bg-white text-[#4A5550] border-[#C4CFC9] hover:border-[#264839]"}`;
  const clearAll = () => { setTagInput(""); setQInput(""); updateParams({ tq: "", q: "", tags: "", country_code: "", state_code: "", supplier_type: "", verified: false, oem: false }); };
  const clearSearch = () => { setQInput(""); updateParams({ q: "" }); };
  const tagLabel = (tag) => (allTagFilters.find((t) => t.tag === tag)?.label) || tag.replace(/-/g, " ");
  const activeChips = [
    ...(textQuery ? [{ key: "q", group: "Search", label: textQuery, onRemove: () => { setQInput(""); updateParams({ q: "" }); } }] : []),
    ...(tagQuery ? [{ key: "tq", group: "Product", label: tagQuery, onRemove: () => { setTagInput(""); updateParams({ tq: "" }); } }] : []),
    ...(activeCountry ? [{ key: "country", group: "Country", label: orderedCountries.find((c) => c.code === activeCountry)?.name || activeCountry, onRemove: () => updateParams({ country_code: "", state_code: "" }) }] : []),
    ...(activeState ? [{ key: "state", group: "State", label: activeState, onRemove: () => updateParams({ state_code: "" }) }] : []),
    ...activeSupplierTypes.map((t) => ({ key: `type-${t.toLowerCase()}`, group: "Type", label: t, onRemove: () => updateParams({ supplier_type: activeSupplierTypes.filter((x) => x !== t).join(",") }) })),
    ...(verifiedOnly ? [{ key: "verified", label: "Verified only", onRemove: () => updateParams({ verified: false }) }] : []),
    ...(customOem ? [{ key: "oem", label: "Custom / OEM", onRemove: () => updateParams({ oem: false }) }] : []),
    ...activeTags.map((t) => ({ key: `tag-${t}`, group: "Tag", label: tagLabel(t), onRemove: () => toggleTag(t) })),
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="category-page">
      <SEO title={seo.title} description={seo.description} path={seo.path} />
      {/* Breadcrumb */}
      <div className="flex items-center text-sm text-[#7D8C85] mb-2">
        <Link to="/categories" className="hover:text-[#264839]">Categories</Link>
        {cat.parent && (
          <>
            <ChevronRight className="w-3 h-3 mx-2" />
            <Link to={categoryPath(cat.parent)} className="hover:text-[#264839]">{cat.parent.name}</Link>
          </>
        )}
        <ChevronRight className="w-3 h-3 mx-2" />
        <span className="text-[#11211A]">{cat.name}</span>
      </div>

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="tracking-overline mb-1">{cat.parent_id === null ? "Parent category" : "Subcategory"}</div>
          <h1 className="font-serif text-3xl md:text-4xl text-[#11211A] tracking-tight">{cat.name}</h1>
          {cat.parent_id === null && cat.launch_status === "launching" && <StatusLine cat={cat} className="mt-2" />}
        </div>
        {cat.description && <p className="text-sm text-[#4A5550] max-w-xl md:text-right line-clamp-2" data-testid="category-description">{cat.description}</p>}
      </div>

      <RfqStrip categoryName={cat.name} onClick={() => setRfqOpen(true)} disabled={listings.items.length === 0 && categoryTop.length === 0} testPrefix="category" />

      {/* Laptop+: subcategory nav above the search bar (unchanged). Phones: search bar first, nav folded behind a toggle below it. */}
      <div className="hidden md:block">
        {cat.parent_id === null
          ? <SubcategoryNav parent={cat} children={cat.children} />
          : <SubcategoryNav parent={cat.parent} children={cat.siblings} activeId={cat.id} compact />}
      </div>

      <SupplierFilterPanel
        q={qInput} onQ={setQInput} onSubmit={submitSearch}
        searchPlaceholder={`Search ${cat.name} — products, company or description…`}
        country={activeCountry} onCountry={(v) => updateParams({ country_code: v, state_code: v ? activeState : "" })}
        countryOptions={orderedCountries.map((c) => ({ value: c.code, label: c.name, count: c.count }))}
        supplierTypes={activeSupplierTypes} onSupplierTypes={(list) => updateParams({ supplier_type: list.join(",") })}
        customOem={customOem} onCustomOem={(on) => updateParams({ oem: on })}
        anyActive={!!anyFilterActive}
        onClear={clearAll}
        advancedCount={advancedCount}
        activeChips={activeChips}
        advancedOpen={advancedOpen}
        onToggleAdvanced={() => setAdvancedOpen((o) => !o)}
        testPrefix="category-filter"
      >
        {hasQuickFilters && (
          <div data-testid="quick-filters">
            <div className="text-xs uppercase tracking-widest text-[#B35C4A] mb-3">Quick filters</div>
            <div className="grid md:grid-cols-3 gap-4">
              {orderedCountries.length > 0 && (
                <div data-testid="qf-country-block">
                  <div className="text-[11px] font-medium text-[#4A5550] mb-1.5 flex items-center gap-1.5">
                    <Globe className="w-3.5 h-3.5 text-[#264839]" /> Country
                    {nearCountry && orderedCountries.some((c) => c.code === nearCountry) && (
                      <span className="text-[9px] uppercase tracking-widest text-[#B35C4A] font-semibold ml-1" data-testid="qf-country-near-you">Yours: {nearCountry}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {orderedCountries.map((c) => {
                      const on = activeCountry === c.code;
                      const isNear = c.code === nearCountry;
                      return (
                        <button key={c.code} type="button" onClick={() => updateParams({ country_code: on ? "" : c.code, state_code: on ? activeState : "" })} className={chipCls(on, isNear)} data-testid={`qf-country-${c.code}`} title={c.name}>
                          {isNear && <span className="w-1.5 h-1.5 rounded-full bg-[#B35C4A]" aria-hidden="true" />}
                          {c.code}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {orderedStates.length > 0 && (
                <div data-testid="qf-state-block">
                  <div className="text-[11px] font-medium text-[#4A5550] mb-1.5 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-[#264839]" /> State / Province
                    {nearCode && orderedStates.some((s) => s.code === nearCode) && (
                      <span className="text-[9px] uppercase tracking-widest text-[#B35C4A] font-semibold ml-1" data-testid="qf-state-near-you">Near you: {nearCode}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {orderedStates.map((s) => {
                      const on = activeState === s.code;
                      const isNear = s.code === nearCode;
                      return (
                        <button key={s.code} type="button" onClick={() => updateParams({ state_code: on ? "" : s.code })} className={chipCls(on, isNear)} data-testid={`qf-state-${s.code}`}>
                          {isNear && <span className="w-1.5 h-1.5 rounded-full bg-[#B35C4A]" aria-hidden="true" />}
                          {s.code}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div data-testid="qf-verified-block">
                <div className="text-[11px] font-medium text-[#4A5550] mb-1.5 flex items-center gap-1.5">
                  <BadgeCheck className="w-3.5 h-3.5 text-[#264839]" /> Trust
                </div>
                <button type="button" onClick={() => updateParams({ verified: !verifiedOnly })} className={chipCls(verifiedOnly)} data-testid="qf-verified-toggle" disabled={qf.verified_count === 0} title={qf.verified_count === 0 ? "No verified suppliers listed yet in this category" : ""}>
                  <BadgeCheck className="w-3 h-3" /> Verified only
                </button>
                {qf.verified_count === 0 && (
                  <div className="text-[10px] text-[#7D8C85] mt-1 italic">Verification badges unlock as suppliers complete their compliance profile.</div>
                )}
              </div>
            </div>
          </div>
        )}
        {visibleTagFilters.length > 0 && (
          <div className={hasQuickFilters ? "mt-5 pt-4 border-t border-[#E8ECE9]" : ""} data-testid="tag-filters">
            <div className="text-xs uppercase tracking-widest text-[#B35C4A] mb-3 flex items-center gap-2">
              Filter by product type
              {activeTags.length > 0 && (
                <button type="button" onClick={() => setActiveTags([])} className="text-[#B35C4A] hover:underline text-[10px] normal-case tracking-normal" data-testid="tag-filter-clear">
                  Clear all
                </button>
              )}
            </div>
            <div className="space-y-3">
              {groupOrder.map((g) => (
                <div key={g}>
                  {groupOrder.length > 1 && <div className="text-[10px] uppercase tracking-widest text-[#7D8C85] mb-1.5">{g}</div>}
                  <div className="flex flex-wrap gap-2">
                    {grouped[g].map((tf) => {
                      const on = activeTags.includes(tf.tag);
                      return (
                        <button key={tf.tag} type="button" onClick={() => toggleTag(tf.tag)} className={`text-xs px-3 py-1.5 border transition inline-flex items-center gap-1.5 ${on ? "bg-[#264839] text-white border-[#264839]" : "bg-white text-[#4A5550] border-[#C4CFC9] hover:border-[#264839]"}`} data-testid={`tag-filter-${tf.tag}`}>
                          {tf.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {allTagFilters.length > visibleTagFilters.length && (
              <div className="mt-3 text-[11px] text-[#7D8C85] italic flex items-center gap-1.5" data-testid="sparse-guard-hint">
                <Sparkles className="w-3 h-3" />
                {allTagFilters.length - visibleTagFilters.length} more tag{allTagFilters.length - visibleTagFilters.length === 1 ? "" : "s"} will unlock once more suppliers list under them.
              </div>
            )}
          </div>
        )}
      </SupplierFilterPanel>

      {((cat.parent_id === null ? cat.children : cat.siblings) || []).some((c) => !c.is_coming_soon) && (
        <div className="md:hidden mb-4" data-testid="mobile-subcategory-fold">
          <button
            type="button"
            onClick={() => setSubnavOpen((o) => !o)}
            aria-expanded={subnavOpen}
            className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-white border border-[#E8ECE9] text-sm font-medium text-[#264839]"
            data-testid="mobile-subcategory-toggle"
          >
            <span className="inline-flex items-center gap-2"><LayoutGrid className="w-4 h-4" /> Browse by subcategory</span>
            {subnavOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {subnavOpen && (
            <div className="mt-2" data-testid="mobile-subcategory-list">
              {cat.parent_id === null
                ? <SubcategoryNav parent={cat} children={cat.children} compact />
                : <SubcategoryNav parent={cat.parent} children={cat.siblings} activeId={cat.id} compact />}
            </div>
          )}
        </div>
      )}

      {tagQuery && (
        <div className="mb-3 text-sm text-[#4A5550] flex flex-wrap items-center gap-2" data-testid="category-tag-search-status">
          <span>Showing suppliers tagged with “{tagQuery}”</span>
          <button type="button" onClick={() => { setTagInput(""); updateParams({ tq: "" }); }} className="text-[#B35C4A] hover:underline text-xs" data-testid="category-tag-search-reset">Show all {cat.name}</button>
        </div>
      )}
      {/* Listings grid */}
      <section>
        <div className="flex items-center justify-between mb-6">
          <div className="text-sm font-medium text-[#11211A]" data-testid="listing-count">
            {textQuery
              ? <span data-testid="search-result-summary">{listings.total} supplier{listings.total === 1 ? "" : "s"} for “{textQuery}”</span>
              : <>{cat.parent_id === null && cat.launch_status === "launching" ? "Launching now, suppliers joining" : "Suppliers"}{anyFilterActive ? " · filtered" : ""}</>}
          </div>
        </div>
        {listings.items.length === 0 ? (
          <div className="bg-[#F0EFEA] p-12 text-center text-[#7D8C85] rounded-sm" data-testid="category-empty-state">
            {textQuery ? (
              <>
                <div className="font-serif text-xl text-[#11211A]" data-testid="search-empty-title">No suppliers found for “{textQuery}”</div>
                <div className="text-sm mt-2">Try another word, or send one request and let the top {cat.name} suppliers come to you.</div>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-4">
                  <button type="button" onClick={() => setRfqOpen(true)} className="btn-primary" data-testid="search-empty-rfq">Request quotes</button>
                  <button type="button" onClick={clearSearch} className="text-sm text-[#B35C4A] hover:underline" data-testid="search-empty-clear">Clear search</button>
                </div>
              </>
            ) : tagQuery ? (
              <>
                <div className="font-serif text-xl text-[#11211A]">No suppliers tagged “{tagQuery}” yet.</div>
                <div className="text-sm mt-2">Try a broader term (e.g. “towels” instead of “bath towels”), or send one RFQ to the top suppliers in {cat.name} and let them respond.</div>
                <div className="mt-5 flex flex-wrap justify-center gap-3">
                  <button type="button" onClick={() => { setTagInput(""); updateParams({ tq: "" }); }} className="btn-secondary" data-testid="category-empty-clear-search">Show all {cat.name}</button>
                  <button type="button" onClick={() => setRfqOpen(true)} className="btn-primary" data-testid="category-empty-rfq">Request quotes instead</button>
                </div>
              </>
            ) : "No suppliers found for this filter."}
          </div>
        ) : (
          <>
            <ListingGrid items={listings.items} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" />
            <div className="mt-8 flex flex-col items-center gap-3" data-testid="category-pagination">
              {listings.items.length < listings.total && (
                <button type="button" onClick={loadMore} disabled={loadingMore} className="btn-secondary disabled:opacity-50" data-testid="category-load-more">
                  {loadingMore ? "Loading…" : "Load more suppliers"}
                </button>
              )}
            </div>
          </>
        )}
      </section>

      {rfqOpen && (
        <CategoryRFQModal
          open={rfqOpen}
          onClose={() => setRfqOpen(false)}
          category={cat}
          topListings={(listings.items.length ? listings.items : categoryTop).slice(0, 4)}
        />
      )}
    </div>
  );
}
